import { Link } from '@tanstack/react-router'
import { domAnimation, LazyMotion, m } from 'motion/react'

import Logo from '@/assets/logo.webp'
import OAKOICover from '@/assets/oakoi-cover.webp'
import MaterialSymbolsCheckRounded from '~icons/material-symbols/check-rounded'
import MdiTable from '~icons/mdi/table'

interface ThankYouStepProps {
  profileData?: { fullName: string } | null
  pictureUrl?: string | null
  checkInDetail?: { table?: string | null } | null
}

export function ThankYouStep({
  profileData,
  pictureUrl,
  checkInDetail,
}: ThankYouStepProps) {
  return (
    <LazyMotion features={domAnimation}>
      <div className="pt-safe relative flex h-dvh w-full flex-col gap-4 overflow-hidden overscroll-none bg-[#eb9fa5] text-[#344C3D] select-none">
        {/* Background texture layers */}
        <div className="botanical-bg absolute inset-0 opacity-20" />
        <div className="floral-overlay opacity-[0.03]" />

        {/* Ambient blur orbs */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <m.div
            animate={{
              scale: [1, 1.15, 1],
              opacity: [0.3, 0.5, 0.3],
              x: [0, 20, 0],
              y: [0, 15, 0],
            }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
            className="bg-blush-soft absolute -top-32 -left-32 size-125 rounded-full blur-[100px]"
          />
          <m.div
            animate={{
              scale: [1, 1.25, 1],
              opacity: [0.15, 0.3, 0.15],
              x: [0, -20, 0],
              y: [0, 30, 0],
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 2,
            }}
            className="bg-sage-light/30 absolute top-1/3 -right-32 size-150 rounded-full blur-[120px]"
          />
        </div>

        <div className="relative z-10 flex shrink-0 flex-col items-center gap-4 pt-12">
          <img
            draggable={false}
            src={Logo}
            alt="OA & KOI Wedding Logo"
            className="h-32 object-contain opacity-20 brightness-0"
          />
          <div className="flex flex-col items-center justify-center pt-4">
            <h1 className="text-center text-4xl font-semibold uppercase">
              ขอบคุณที่มาร่วมงาน
            </h1>
            {profileData && (
              <p className="text-center text-xl">
                ยินดีต้อนรับคุณ {profileData.fullName}
              </p>
            )}
          </div>
          <div className="mt-4 flex flex-col items-center gap-6">
            {pictureUrl && (
              <div className="relative">
                <img
                  draggable={false}
                  src={pictureUrl}
                  alt="Profile"
                  className="size-32 rounded-full border-4 border-white object-cover shadow-xl"
                />
                <div className="absolute -right-2 bottom-0 flex size-10 items-center justify-center rounded-full bg-[#00B900] text-white shadow-lg ring-4 ring-white">
                  <MaterialSymbolsCheckRounded className="size-8" />
                </div>
              </div>
            )}
            <div className="flex flex-col items-center gap-2 text-center">
              <p className="text-center text-xl font-bold">
                {checkInDetail?.table
                  ? `โต๊ะของคุณคือหมายเลข ${checkInDetail.table}`
                  : 'เจ้าหน้าที่จะแจ้งโต๊ะที่หน้างาน'}
              </p>
              <p className="text-center text-lg opacity-80">
                รายละเอียดจะถูกส่งไปใน LINE
              </p>
              <Link
                to="/map"
                className="text-primary mt-4 flex items-center gap-2 rounded-full bg-white/90 px-6 py-2 text-sm font-bold shadow-lg transition-all hover:bg-white active:scale-95"
              >
                <MdiTable className="size-5" />
                <span>ดูแผนผังที่นั่ง</span>
              </Link>
            </div>
          </div>
        </div>
        <div className="relative z-10 flex flex-1 items-end justify-center">
          <img
            draggable={false}
            src={OAKOICover}
            alt="OA & KOI"
            className="h-full object-cover object-top"
          />
        </div>
      </div>
    </LazyMotion>
  )
}
